/**
 * STORE-SPLIT slice 5 — the initial / demo session-shell + geometry-document
 * builders, the seed fixtures they read (`defaultObjectProperties`,
 * `sessionUsers`, `spaces`), the session-hydration helpers, and the
 * current-layer reconciliation guard, extracted VERBATIM from `editorStore.ts`
 * (no behaviour change; same STORE-SPLIT lineage as slices 1-4).
 *
 * These are PURE builders / reducers (no zustand `set`/`get`, no
 * `useEditorStore`, no module-mutable state). `editorStore.ts` imports back the
 * four symbols its initializer + dispatch chokepoint use
 * (`buildInitialEditorShellState`, `buildInitialGeometryDocument`,
 * `hydrateEditorSessionShellState`, `reconcileCurrentLayerAgainstTable`) and
 * RE-EXPORTS the three former public builders + the `HydrateSessionShellInput`
 * type so external import paths (test suites, App seams) stay unchanged.
 *
 * This module imports ONLY leaf modules (drawing-model, contracts, ../i18n,
 * ./cecolorStore) — none import editorStore.ts, so the deps graph stays acyclic
 * (verified by `npm run deps:graph`).
 *
 * NOTE — the pre-existing, deliberately-unused `_entities` demo fixture
 * (306 lines, `_`-prefixed to satisfy no-unused-vars) is NOT moved here: it is
 * not referenced by any builder below, so it stays in `editorStore.ts` untouched
 * rather than relocating dead code.
 */
import {
  drawingDocumentSchema,
  type DrawingDocument,
} from "@dxf-ai-editor/drawing-model";
import {
  bootstrapAbsoluteAdminEmail,
  editorShellStateSchema,
  type DrawingSpace,
  type DynamicBlockCommand,
  type EditorCommand,
  type EditorShellState,
  type SessionUser,
} from "@dxf-ai-editor/contracts";
import { t } from "../i18n";
import { DEFAULT_CE_COLOR } from "./cecolorStore";

export type HydrateSessionShellInput = {
  bootstrapAbsoluteAdminEmail: string;
  users: SessionUser[];
  preferredUserEmail?: string | null;
};

const defaultObjectProperties = {
  Layer: "0",
  Color: "ByLayer",
  Linetype: "ByLayer",
  Lineweight: "ByLayer",
  Transparency: "0",
  Annotative: "No",
} as const;

/** Re-point the current-LAYER (CLAYER) pointer to `nextName` (fast path: same ref). */
function withCurrentLayer(
  shellState: EditorShellState,
  nextName: string,
): EditorShellState {
  if (shellState.propertyWorkflows.defaultObjectProperties.Layer === nextName) {
    return shellState;
  }
  return {
    ...shellState,
    propertyWorkflows: {
      ...shellState.propertyWorkflows,
      defaultObjectProperties: {
        ...shellState.propertyWorkflows.defaultObjectProperties,
        Layer: nextName,
      },
    },
  };
}

/**
 * Lane J (D3-1) — @codex round-2 P2 (findings 2 + 3), the current-layer
 * invariant. `propertyWorkflows.defaultObjectProperties.Layer` (AutoCAD CLAYER —
 * the layer new geometry is drawn on, set by `setCurrentLayer`) MUST always name
 * an EXISTING entry in the layer table. `setCurrentLayer` dispatches
 * `properties/apply` with an EMPTY selection (T7, agent-bridge — mirrors
 * `setCurrentDimensionStyle`): it IS a real dispatch now (audited, flows through
 * the Command Engine chokepoint), but is STILL not a table edit and does NOT
 * push a history entry / clear the redo stack (`computeInverse` returns null for
 * a no-selection `properties/apply`) — so a LATER dispatch that SHRINKS the
 * layer table can still strand the pointer at a layer that no longer exists —
 * and subsequent drafting would then create entities on a phantom layer.
 * Concrete strandings this reconciles, all at the ONE universal chokepoint
 * (covers forward + undo + redo in a single check, rather than per-command
 * guards the bypass replays would skip):
 *   - finding 3 (UNDO of an add): addLayer("Walls") → setCurrentLayer("Walls")
 *     → undo restores the pre-add table WITHOUT "Walls" (CLAYER now dangles);
 *   - finding 2 (REDO of a delete): deleteLayer("Walls") (non-current, allowed)
 *     → undo → setCurrentLayer("Walls") → redo replays `header/delete-layer`
 *     DIRECTLY through the reducer, BYPASSING the wrapper's "cannot delete the
 *     current layer" guard, deleting the now-current "Walls";
 *   - a command-line `header/delete-layer` that bypasses the wrapper guard.
 * Reset target is "0" (undeletable — always present), matching AutoCAD, which
 * falls CLAYER back to layer 0 whenever the current layer is removed.
 *
 * T7 (agent-bridge), local Codex round-5 P2 — `header/rename-layer` on the
 * CURRENT layer is handled FIRST, as a forward-carry (mirrors
 * `reconcileCurrentDimStyleAgainstTable`'s `header/rename-dimension-style`
 * branch EXACTLY): the table row SURVIVES the rename (just under a new name),
 * so CLAYER should follow it to `newName`, not fall back to "0" — a rename is
 * not a removal. Without this, renaming your current layer via the AI action
 * or the Layer Manager dialog would silently redirect every subsequently
 * created entity onto layer "0" instead of the (renamed) layer the user was
 * actually working on. The `header/restore-style-tables-snapshot` UNDO half
 * of that carry is handled next (`command.currentLayerName`, mirrors
 * `currentDimensionStyle` exactly) — without it, UNDOING a current-layer
 * rename would fall CLAYER back to "0" via the generic fallback below
 * (the restored table no longer carries `newName`), instead of back to the
 * pre-rename name.
 *
 * Returns the SAME state reference on the fast path (CLAYER is "0"/absent, the
 * layer still exists, or an unrelated command) so unaffected dispatches
 * allocate nothing.
 */
export function reconcileCurrentLayerAgainstTable(
  shellState: EditorShellState,
  command: EditorCommand | DynamicBlockCommand,
  nextDoc: DrawingDocument,
): EditorShellState {
  const currentLayer =
    shellState.propertyWorkflows.defaultObjectProperties.Layer;
  if (
    command.type === "header/rename-layer" &&
    currentLayer !== undefined &&
    // Local Codex round-22 P1 (github PR #527 review) — case-insensitive
    // match, mirroring the reducer's own `namesEqualCaseInsensitive` oldName
    // lookup (round-21). A bare `===` here missed the carry whenever the
    // AI/raw dispatch's `oldName` differed only in case from CLAYER's actual
    // (canonical) stored spelling — e.g. CLAYER="Walls",
    // RENAME_LAYER{oldName:"walls",...} — the reducer still renamed "Walls"
    // (case-insensitive lookup), but this exact check never fired, so the
    // carry below never ran and the generic stranding check at the bottom
    // reset CLAYER to "0" (the renamed row's OLD name "Walls" is gone from
    // the table — it's "Partitions" now — so the fallback saw a "missing"
    // pointer instead of a "renamed" one).
    namesEqualCaseInsensitive(currentLayer, command.oldName.trim())
  ) {
    // Local Codex round-6 P2 — verify the rename ACTUALLY SUCCEEDED before
    // carrying CLAYER. The AI safe contour dispatches `header/rename-layer`
    // directly, bypassing the `renameLayer` wrapper's own validation
    // (empty/duplicate `newName`, nonexistent `oldName`, layer "0", self-
    // rename); the reducer's fail-closed backstop then no-ops (table
    // unchanged) for an invalid raw dispatch. Without this check, a
    // REJECTED rename (e.g. `renameLayer("Walls", "Doors")` while "Doors"
    // already names a DIFFERENT layer) would still blindly move CLAYER to
    // "Doors" — turning a no-op into an unintended active-layer change with
    // no history entry. `newName` must now be a live row AND `oldName` must
    // no longer be one — the exact post-condition the reducer's successful
    // rename guarantees (`buildBaseDoc` mirrors this pattern's twin: an
    // idempotent no-op that changes nothing).
    //
    // Round-22 P1 — the "old name gone" half uses `currentLayer` itself
    // (NOT `command.oldName.trim()`): the outer condition above already
    // proved `currentLayer` case-insensitively matches the command's
    // `oldName`, and CLAYER's own invariant (maintained by this function on
    // every dispatch) guarantees `currentLayer` is ALWAYS the row's true,
    // canonically-cased live name — so it is the correct value to look for
    // post-rename, regardless of what casing the command's raw `oldName`
    // happened to carry.
    const trimmedNewName = command.newName.trim();
    const nextLayers = nextDoc.styleTables?.layers ?? [];
    const renameSucceeded =
      nextLayers.some((l) => l.name === trimmedNewName) &&
      !nextLayers.some((l) => l.name === currentLayer);
    if (renameSucceeded) {
      return withCurrentLayer(shellState, trimmedNewName);
    }
    // Rejected — fall through to the generic stranding check below (the
    // unchanged table still carries `currentLayer`'s canonical name, so
    // that check's `layers.some(...)` finds it and returns the SAME
    // reference — a true no-op, matching the reducer's own no-op).
  }
  if (
    command.type === "header/restore-style-tables-snapshot" &&
    command.currentLayerName !== undefined
  ) {
    // T7, local Codex round-5 P2 — restore the pre-rename CLAYER ONLY when
    // the live pointer STILL equals the value the forward rename set it to
    // (mirrors `currentDimensionStyleForwardValue`'s guard exactly): a
    // deliberate `setCurrentLayer` to a DIFFERENT live layer AFTER the
    // rename (a non-undoable CLAYER-sysvar change) survives the undo
    // instead of being clobbered back to the pre-rename value.
    const forwardValue = command.currentLayerForwardValue;
    if (forwardValue === undefined || currentLayer === forwardValue) {
      return withCurrentLayer(shellState, command.currentLayerName);
    }
    return shellState;
  }
  // "0" always exists (undeletable); an absent/blank CLAYER needs no reset.
  if (!currentLayer || currentLayer === "0") return shellState;
  const layers = nextDoc.styleTables?.layers ?? [];
  // Round-26 P2 (github PR #527 review) — resolve case-insensitively,
  // mirroring `namesEqualCaseInsensitive` everywhere else a layer name is
  // matched (round-21/22/23/25). This UNIVERSAL generic fallback runs after
  // EVERY dispatch (not just `header/rename-layer`), so it is the LAST line
  // of defense the OPEN/import-merge path relies on: `stampCurrentLayer
  // OntoCreateCommand` (round-25) correctly stamps the FIRST post-merge
  // entity's `layer` field case-insensitively, but this reconcile runs
  // RIGHT AFTER that same dispatch — with a bare `===` it still saw the
  // preserved CLAYER ("Walls") as "missing" from a table that only has
  // "walls", and reset the POINTER to "0" — so every entity created AFTER
  // the first one landed on "0" instead of the merged document's actual
  // layer, even though the first entity was stamped correctly.
  const matchedLayer = layers.find((l) =>
    namesEqualCaseInsensitive(l.name, currentLayer),
  );
  if (matchedLayer === undefined) return withCurrentLayer(shellState, "0");
  // `withCurrentLayer`'s own fast path already returns the SAME reference
  // when `matchedLayer.name === currentLayer` (the common exact-match
  // case), so this preserves the "no allocation on the fast path"
  // guarantee for every unaffected dispatch.
  return withCurrentLayer(shellState, matchedLayer.name);
}

/** Re-point the current-DIMSTYLE pointer to `nextName` (fast path: same ref). */
function withCurrentDimStyle(
  shellState: EditorShellState,
  nextName: string,
): EditorShellState {
  if (
    shellState.propertyWorkflows.defaultObjectProperties["Dimension style"] ===
    nextName
  ) {
    return shellState;
  }
  return {
    ...shellState,
    propertyWorkflows: {
      ...shellState.propertyWorkflows,
      defaultObjectProperties: {
        ...shellState.propertyWorkflows.defaultObjectProperties,
        "Dimension style": nextName,
      },
    },
  };
}

/**
 * Local Codex round-21 P2 (github PR #527 review) — AutoCAD style/layer TABLE
 * names (DXF group-2 SYMBOL table entries — LAYER, DIMSTYLE, MLEADERSTYLE,
 * STYLE, …) are compared CASE-INSENSITIVELY: "Walls" and "walls" name the
 * SAME layer, never two distinct rows. `packages/drawing-model/src/
 * referenceAudit.resolution.ts`'s DUPLICATE_TABLE_ENTRY_NAME audit already
 * flags this pair as one duplicate (see `referenceAudit.test.ts`'s
 * "case-insensitive" cases) — every add/rename/collision/remap/current-
 * pointer-reconcile site touching a table name must agree with that audit,
 * not silently create the exact ambiguity it exists to catch. A bare
 * `.toLowerCase()` fold, matching the audit's own comparison exactly (no
 * Unicode normalization — table names are short, ASCII-biased DXF SYMBOL
 * identifiers in practice).
 *
 * Lives here (not styleTablesReducer.ts, where round-21 first introduced it)
 * so BOTH styleTablesReducer.ts (the table-mutation reducer) and this
 * module's `reconcileCurrentLayerAgainstTable` (the CLAYER current-pointer
 * reconcile, round-22 P1/P2 fix) can share the ONE definition —
 * styleTablesReducer.ts already imports `canonicalStandardDimStyleName` from
 * here, so this is the established import direction (reversing it would
 * cycle).
 */
export function namesEqualCaseInsensitive(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * @codex P2 — resolve the CANONICAL spelling of the mandatory "Standard" DIMSTYLE
 * from the LIVE table. DIMSTYLE names fold (an imported drawing may legitimately
 * store the mandatory style as `STANDARD`/mixed case), yet the current-DIMSTYLE
 * pointer is EXACT-compared to table / `<option>` names by the quickbar select and
 * the manager "Set Current" marker — so hard-coding the literal `"Standard"` when
 * the table spells it `STANDARD` strands the pointer at a name no row carries,
 * blanking (or mis-pointing) the current-style UI until the user re-picks it.
 * Return the table's OWN spelling of the standard style, folding case; fall back to
 * the literal `"Standard"` only when the table truly has no standard entry (the
 * same string the reducer's orphaned-dimension remap uses, which resolves case-
 * insensitively so it stays consistent either way).
 */
export function canonicalStandardDimStyleName(
  dimensionStyles: ReadonlyArray<{ name: string }> | undefined,
): string {
  return (
    (dimensionStyles ?? []).find((s) => s.name.toLowerCase() === "standard")
      ?.name ?? "Standard"
  );
}

/**
 * @codex P2 — keep the CURRENT DIMSTYLE pointer
 * (`defaultObjectProperties["Dimension style"]`, the AutoCAD DIMSTYLE sysvar — the
 * style new DIMENSION entities inherit) in lockstep with a dimension-style
 * delete / rename AT THE UNIVERSAL DISPATCH CHOKEPOINT, so it stays consistent
 * across FORWARD + UNDO + REDO (all three route here) — the sibling of
 * `reconcileCurrentLayerAgainstTable` for the DIMSTYLE sysvar.
 *
 * The pointer is SHELL state, not a document slice, so before this it was
 * re-pointed by a SEPARATE out-of-band `properties/apply` in the store wrapper
 * AFTER the undoable rename/delete dispatch — which left it OUTSIDE the rename's
 * history entry. Undoing the rename then restored the table to the old name while
 * the pointer stayed at the new, now-missing name (new dimensions fell back to the
 * first style instead of the restored current style). Folding the reconcile into
 * the chokepoint fixes that: the forward/redo commands re-point deterministically,
 * and the rename/delete UNDO inverse (`header/restore-style-tables-snapshot`)
 * carries the PRE-mutation pointer in `currentDimensionStyle` so undo restores it.
 *
 *   - `header/rename-dimension-style`: current === oldName → re-point to newName
 *     (matches the reducer's exact-trimmed entity remap `styleName === oldName`);
 *   - `header/delete-dimension-style`: current === styleName → fall back to the
 *     CANONICAL "Standard" spelling in the post-delete table (`canonicalStandard
 *     DimStyleName`, folding case — the SAME target orphaned dimensions get on
 *     delete; a literal "Standard" would strand the exact-compare pointer UI when
 *     the imported table spells the mandatory style `STANDARD`/mixed case);
 *   - `header/restore-style-tables-snapshot`: `currentDimensionStyle` present
 *     (undo of a pointer-moving delete/rename) → restore that pre-mutation value,
 *     but ONLY when the live pointer still equals `currentDimensionStyleForwardValue`
 *     (a later `Set Current` to another live style survives the undo);
 *   - `header/restore-style-tables-snapshot` WITHOUT `currentDimensionStyle`
 *     (undo of an add, or any table restore): reconcile the pointer against the
 *     restored `styleTables.dimensionStyles` — if it no longer names a live style,
 *     fall back to "Standard" (else the pointer strands at a dropped name).
 *
 * Returns the SAME state reference on the fast path (unrelated command, or the
 * current style was NOT the affected one) so unaffected dispatches allocate
 * nothing.
 */
export function reconcileCurrentDimStyleAgainstTable(
  shellState: EditorShellState,
  command: EditorCommand | DynamicBlockCommand,
  nextDoc: DrawingDocument,
): EditorShellState {
  const current =
    shellState.propertyWorkflows.defaultObjectProperties["Dimension style"];
  if (command.type === "header/rename-dimension-style") {
    const oldName = command.oldName.trim();
    if (current !== undefined && current === oldName) {
      return withCurrentDimStyle(shellState, command.newName.trim());
    }
    return shellState;
  }
  if (command.type === "header/delete-dimension-style") {
    if (current !== undefined && current === command.styleName.trim()) {
      // @codex P2 — the mandatory "Standard" style cannot be deleted, so the
      // post-delete table still carries it; resolve its CANONICAL spelling (folds
      // `STANDARD`/mixed case) so the pointer names a real row for the exact-compare
      // quickbar/manager UI. Falls back to the literal "Standard" only when absent.
      return withCurrentDimStyle(
        shellState,
        canonicalStandardDimStyleName(nextDoc.styleTables?.dimensionStyles),
      );
    }
    return shellState;
  }
  if (command.type === "header/restore-style-tables-snapshot") {
    const restore = command.currentDimensionStyle;
    if (restore !== undefined) {
      // @codex P2 — restore the pre-op pointer ONLY when the live pointer STILL
      // equals the value the forward op set it to (rename → new name, delete →
      // "Standard"). A deliberate `Set Current` to ANOTHER live style AFTER the
      // table edit is a non-undoable DIMSTYLE-sysvar change: undoing the earlier
      // rename/delete must NOT clobber it back to the pre-op value.
      const forwardValue = command.currentDimensionStyleForwardValue;
      if (forwardValue === undefined || current === forwardValue) {
        return withCurrentDimStyle(shellState, restore);
      }
      return shellState;
    }
    // @codex P2 — no carried pointer, but an undo that RESTORES the DIMSTYLE table
    // (an add/delete/rename inverse) can strand the current pointer at a name no
    // longer in the restored table — e.g. add `Arch` → Set Current `Arch` → undo
    // the add: the pointer keeps naming `Arch` though the restored table dropped it,
    // and re-adding `Arch` later silently makes it current again. Reconcile the
    // pointer against the RESTORED table: if it no longer names a live style (case-
    // insensitively — DIMSTYLE names fold), fall back to the CANONICAL "Standard"
    // spelling in the restored table (folds `STANDARD`/mixed case so the pointer
    // names a real row). Only a real dimension-style-table restore
    // carries `styleTables.dimensionStyles`; the layout-plot inverse (no styleTables)
    // leaves the pointer untouched.
    const restoredDimensionStyles = (
      command.styleTables as
        | { dimensionStyles?: ReadonlyArray<{ name: string }> }
        | undefined
    )?.dimensionStyles;
    if (
      restoredDimensionStyles !== undefined &&
      current !== undefined &&
      !restoredDimensionStyles.some(
        (s) => s.name.toLowerCase() === current.toLowerCase(),
      )
    ) {
      // @codex P2 — canonical "Standard" from the RESTORED table (folds case) so
      // the pointer names a real row for the exact-compare quickbar/manager UI.
      return withCurrentDimStyle(
        shellState,
        canonicalStandardDimStyleName(restoredDimensionStyles),
      );
    }
  }
  return shellState;
}

/**
 * @codex #499 P2 — the OPEN / INSERT-import sibling of
 * `reconcileCurrentDimStyleAgainstTable`. A file open or an import MERGE rebuilds
 * shell state through `syncShellStateFromMergedGeometryDocument`, which PRESERVES
 * `propertyWorkflows` verbatim (so the matchprop / current-pointer flows survive)
 * — it does NOT flow through the dispatch reconcile. So a current DIMSTYLE the user
 * set in a PRIOR drawing survives into the freshly-opened / merged document even
 * when that document does not carry the style, and the create-dimension stamp then
 * references a phantom style name (or silently binds new dimensions to the previous
 * drawing's leftover style). Reset the pointer to the merged document's CANONICAL
 * "Standard" (folding case — DIMSTYLE names are case-insensitive) whenever it no
 * longer names a live dimension style there, so the pointer always names a REAL row
 * of the current document — the exact invariant `reconcileCurrentLayerAgainstTable`
 * enforces for CLAYER on the dispatch path.
 *
 * Returns the SAME reference when the pointer is unset or still names a live style,
 * so the hot merge path (huge-document imports) allocates nothing.
 */
export function reconcileCurrentDimStyleAgainstMergedDocument(
  shellState: EditorShellState,
  mergedDoc: DrawingDocument,
): EditorShellState {
  const current =
    shellState.propertyWorkflows.defaultObjectProperties["Dimension style"];
  if (current === undefined) return shellState;
  const dimStyles = mergedDoc.styleTables?.dimensionStyles ?? [];
  const lowered = current.toLowerCase();
  if (dimStyles.some((s) => s.name.toLowerCase() === lowered)) {
    return shellState;
  }
  return withCurrentDimStyle(shellState, canonicalStandardDimStyleName(dimStyles));
}

const sessionUsers: SessionUser[] = [
  {
    email: bootstrapAbsoluteAdminEmail,
    displayName: "Mikhail Moskalev",
    role: "absolute_admin",
    status: "active",
  },
  {
    email: "editor.user@gmail.com",
    displayName: "Editor Session",
    role: "editor",
    status: "active",
  },
  {
    email: "viewer.user@gmail.com",
    displayName: "Viewer Session",
    role: "viewer",
    status: "active",
  },
  {
    email: "suspended.user@gmail.com",
    displayName: "Suspended Session",
    role: "editor",
    status: "suspended",
  },
];

const spaces: DrawingSpace[] = [
  {
    id: "model",
    name: "Model",
    kind: "model",
    pageSetup: t("demo.space.model_setup"),
    viewports: [],
  },
  {
    id: "layout-1",
    name: "Layout 1",
    kind: "layout",
    pageSetup: "A1 1:100",
    viewports: [
      {
        id: "vp-main",
        name: t("demo.viewport.main_plan"),
        scale: "1:100",
        status: "active",
        clipped: false,
        overrideLayerCount: 3,
      },
      {
        id: "vp-detail",
        name: t("demo.viewport.detail_callout"),
        scale: "1:25",
        status: "locked",
        clipped: true,
        overrideLayerCount: 2,
      },
    ],
  },
  {
    id: "layout-2",
    name: "Layout 2",
    kind: "layout",
    pageSetup: t("demo.space.a3_issue"),
    viewports: [
      {
        id: "vp-assembly",
        name: t("demo.viewport.assembly"),
        scale: "1:50",
        status: "planned",
        clipped: false,
        overrideLayerCount: 1,
      },
    ],
  },
];

export function buildInitialEditorShellState(): EditorShellState {
  return editorShellStateSchema.parse({
    version: 1,
    session: {
      currentUserEmail: "editor.user@gmail.com",
      bootstrapAbsoluteAdminEmail,
      users: sessionUsers,
    },
    drawing: {
      id: "dwg-stage-a-shell",
      title: t("demo.drawing.title"),
      spaces,
      activeSpaceId: "model",
      layoutContext: "model",
      entities: [],
    },
    ui: {
      activePanel: "properties",
    },
    propertyWorkflows: {
      defaultObjectProperties: {
        ...defaultObjectProperties,
      },
      matchpropSourceEntityId: null,
    },
    selection: {
      entityIds: [],
      previousPickset: [],
    },
    commandLine: {
      draft: "",
      lastSubmitted: null,
      transcript: [
        {
          id: "tx-0",
          tone: "info",
          message: t("demo.transcript.shell_ready"),
        },
      ],
    },
    audit: {
      nextSequence: 1,
      events: [],
    },
  });
}

export function buildInitialGeometryDocument(): DrawingDocument {
  return drawingDocumentSchema.parse({
    id: "dwg-stage-a-geometry",
    title: "Stage A simple geometry core",
    activeSpaceId: "model",
    // CECOLOR (round-8 FINDING B) — a FRESH document seeds the template default
    // ByLayer. CECOLOR is a PER-DRAWING sysvar (Autodesk doc *Saved in: Drawing*,
    // *Initial value: BYLAYER*), NOT a cross-session profile — so a new drawing
    // takes the template default, never a persisted localStorage colour (the
    // round-1 persist + round-7 seed-from-persisted were non-AutoCAD and the root
    // of the reset desync; both removed). `resetShellState` mirrors the live
    // `cecolorStore` to the same ByLayer so doc and live AGREE on new/reset. The
    // OPEN/import path hydrates the live store FROM the file's $CECOLOR
    // (`hydrateCeColorFromImportedDocument`) — `doc.cecolor` is the single truth.
    cecolor: { ...DEFAULT_CE_COLOR },
    spaces: [
      {
        id: "model",
        kind: "model",
        name: "Model",
        entities: [],
      },
      {
        id: "layout-1",
        kind: "layout",
        name: "Layout 1",
        entities: [],
      },
      {
        id: "layout-2",
        kind: "layout",
        name: "Layout 2",
        entities: [],
      },
    ],
  });
}

function resolveHydratedCurrentUserEmail(
  state: EditorShellState,
  users: SessionUser[],
  preferredUserEmail?: string | null,
): string {
  const candidateEmails = [
    preferredUserEmail ?? undefined,
    state.session.currentUserEmail,
    users.find((user) => user.role === "editor" && user.status === "active")
      ?.email,
    users.find((user) => user.status === "active")?.email,
    users[0]?.email,
  ].filter((candidate): candidate is string => Boolean(candidate));

  return (
    candidateEmails.find((email) =>
      users.some((user) => user.email === email),
    ) ?? state.session.currentUserEmail
  );
}

export function hydrateEditorSessionShellState(
  state: EditorShellState,
  input: HydrateSessionShellInput,
): EditorShellState {
  const users = input.users.length > 0 ? input.users : state.session.users;

  return editorShellStateSchema.parse({
    ...state,
    session: {
      ...state.session,
      currentUserEmail: resolveHydratedCurrentUserEmail(
        state,
        users,
        input.preferredUserEmail,
      ),
      bootstrapAbsoluteAdminEmail: input.bootstrapAbsoluteAdminEmail,
      users,
    },
  });
}
